import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI, paymentsAPI } from '../services/api'
import {
  ArrowLeft,
  CreditCard,
  CheckCircle,
  AlertTriangle,
  Euro,
  Calendar,
  FileText,
  Lock,
  Unlock,
  Download
} from 'lucide-react'

const ApprovePaymentPage = () => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchProject()
  }, [projectId])

  const fetchProject = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.getProject(projectId)

      if (!response.data.isOwner) {
        navigate('/dashboard')
        return
      }

      setProject(response.data.project)
    } catch (error) {
      console.error('Error fetching project:', error)
      setError('Project kon niet worden geladen')
    } finally {
      setLoading(false)
    }
  }

  const handleFinalPayment = async () => {
    const finalAmount = project.payment?.final?.amountIncBtw || project.budget * 0.90 * 1.21

    if (!window.confirm(`Weet je zeker dat je de eindbetaling van ${formatCurrency(finalAmount)} (incl. 21% BTW) wilt voltooien? Je wordt doorgestuurd naar Stripe om te betalen.`)) {
      return
    }

    try {
      setProcessing(true)
      setError('')

      // Create Stripe Checkout session for final payment
      const response = await projectsAPI.approveProject(projectId, {
        paymentAmount: finalAmount
      })

      // Redirect to Stripe Checkout
      const checkoutUrl = response.data?.data?.checkoutUrl
      if (checkoutUrl) {
        window.location.href = checkoutUrl
      } else {
        alert('Betaling succesvol verwerkt!')
        navigate(`/project-workspace/${projectId}`)
      }

    } catch (error) {
      console.error('Error processing payment:', error)
      const errorMessage = error.response?.data?.message || 'Fout bij het verwerken van de betaling'
      setError(errorMessage)
      alert(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[#0F172A] text-xl">Project laden...</div>
      </div>
    )
  }

  if (error && !project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Fout bij laden</h2>
          <p className="text-[#475569] mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-[#3EB489] text-white rounded-lg hover:bg-[#359E78] transition"
          >
            Terug naar Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white text-[#0F172A]">

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-start sm:items-center gap-3 sm:gap-4 mb-8">
          <button
            onClick={() => navigate(`/project-workspace/${projectId}`)}
            className="p-2 hover:bg-[#F8FAFC] rounded-lg transition flex-shrink-0"
            aria-label="Terug naar workspace"
          >
            <ArrowLeft className="w-6 h-6 text-[#0F172A]" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A]">Finale Betaling</h1>
            <p className="text-[#475569]">Voltooi je betaling om alle content vrij te geven</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Project Details */}
          <div className="bg-[#F8FAFC] backdrop-blur rounded-2xl p-6 border border-gray-200 shadow-md">
            <h2 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-3">
              <FileText className="w-6 h-6 text-[#1D8BF8]" />
              Project Details
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-[#0F172A] break-words">{project.title}</h3>
                <p className="text-[#475569] line-clamp-3">{project.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#475569]">Budget</p>
                  <p className="text-[#0F172A] font-semibold">{formatCurrency(project.budget)}</p>
                </div>
                <div>
                  <p className="text-sm text-[#475569]">Status</p>
                  <p className="text-emerald-600 font-semibold">Goedgekeurd</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-[#475569] mb-2">Deliverables</p>
                <div className="space-y-2">
                  {project.deliverables?.map((deliverable, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-lg">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0F172A] font-medium break-words">{deliverable.title}</p>
                        <p className="text-sm text-[#475569]">
                          {deliverable.files?.length || 0} bestand(en)
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          <div className="bg-[#F8FAFC] backdrop-blur rounded-2xl p-6 border border-gray-200 shadow-md">
            <h2 className="text-xl font-semibold text-[#0F172A] mb-6 flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-emerald-600" />
              Betaling Overzicht
            </h2>

            <div className="space-y-6">
              {/* Payment Breakdown */}
              <div className="space-y-4">
                <div className="flex flex-wrap justify-between items-center gap-2 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-[#0F172A] text-sm sm:text-base">Aanbetaling (10% incl. BTW)</span>
                  </div>
                  <span className="text-emerald-600 font-semibold ml-auto whitespace-nowrap">
                    {formatCurrency(project.payment?.deposit?.amountIncBtw || project.budget * 0.10 * 1.21)}
                  </span>
                </div>

                <div className="flex flex-wrap justify-between items-center gap-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <CreditCard className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-[#0F172A] text-sm sm:text-base">Eindbetaling (90% incl. BTW)</span>
                  </div>
                  <span className="text-orange-500 font-semibold ml-auto whitespace-nowrap">
                    {formatCurrency(project.payment?.final?.amountIncBtw || project.budget * 0.90 * 1.21)}
                  </span>
                </div>

                <div className="flex flex-wrap justify-between items-center gap-2 p-3 bg-gray-50 rounded-lg text-sm">
                  <span className="text-[#475569]">Waarvan BTW (21%)</span>
                  <span className="text-[#475569] ml-auto whitespace-nowrap">{formatCurrency(project.payment?.final?.btw || project.budget * 0.90 * 0.21)}</span>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap justify-between items-center gap-2 text-lg font-semibold">
                    <span className="text-[#0F172A]">Totaal Project incl. BTW</span>
                    <span className="text-[#0F172A] ml-auto whitespace-nowrap">{formatCurrency((project.payment?.deposit?.amountIncBtw || project.budget * 0.10 * 1.21) + (project.payment?.final?.amountIncBtw || project.budget * 0.90 * 1.21))}</span>
                  </div>
                </div>
              </div>

              {/* Content Access Warning */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex gap-3">
                  <Lock className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-500 font-semibold mb-1">Content Vergrendeld</p>
                    <p className="text-[#0F172A] text-sm">
                      Alle content blijft beveiligd tot de finale betaling is voltooid.
                      Na betaling wordt alle content vrijgegeven voor download.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Button */}
              <button
                onClick={handleFinalPayment}
                disabled={processing}
                className="w-full flex items-center justify-center gap-3 py-4 px-4 sm:px-6 bg-emerald-600 text-white font-semibold text-sm sm:text-base rounded-lg hover:bg-emerald-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {processing ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin flex-shrink-0" />
                    Verwerken...
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5 flex-shrink-0" />
                    <span>
                      Betaal {formatCurrency(project.payment?.final?.amountIncBtw || project.budget * 0.90 * 1.21)} incl. BTW
                      <span className="hidden sm:inline"> & Ontgrendel Content</span>
                    </span>
                  </>
                )}
              </button>

              <p className="text-xs text-[#475569] text-center">
                Door te klikken ga je akkoord met de betaling en worden alle bestanden
                direct beschikbaar voor download.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-500">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ApprovePaymentPage
